import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {Message} from "primereact/message";
import {ProgressSpinner} from "primereact/progressspinner";
import {Toast} from "primereact/toast";
import {Canvas, FieldInspector, FormSettings, Palette, newField} from "./builder-design";
import {DestinationEditor, FillRulesEditor, PostSubmitEditor, ScoresEditor} from "./builder-logic";
import FormRenderer, {EndingView} from "./form-renderer";
import {formsApi, formsErrorDetails, formsErrorKey} from "./forms-api";
import {useFormsAccess} from "./forms-access";
import {PlanGate} from "./forms-home";
import {Field, Section, StatusTag} from "./forms-ui";
import {Icon, currentWorkspace, useFormat, useT} from "./host";
import Submissions from "./submissions";
import {publicDefinition} from "./engine";

const STEPS = [["design", "mi mi:edit_note"], ["logic", "mi mi:account_tree"], ["responses", "mi mi:inbox"]];
const SAVE_DELAY = 1200;
const DIALOG = {className: "rtl border-round-bottom-2xl", headerClassName: "border-round-top-2xl border-bottom-1 border-300 pb-3 pt-3", contentClassName: "p-4", pt: {footer: () => ({className: "border-round-bottom-2xl"})}};

function ReportLines({report}) {
    const t = useT();
    if (!report) return null;
    const lines = [
        ...report.errors.map((line) => ({...line, severity: "error"})),
        ...report.warnings.map((line) => ({...line, severity: "warn"})),
        ...report.notes.map((line) => ({...line, severity: "info"})),
    ];
    if (!lines.length) return <Message severity="success" className="w-full justify-content-start text-xs" text={t("forms.publish.ready")}/>;
    return (
        <div className="flex flex-column gap-2">
            {lines.map((line, index) => <Message key={index} severity={line.severity} className="w-full justify-content-start text-xs" text={t(`forms.report.${line.code}`, line.params)}/>)}
        </div>
    );
}

function CopyField({value, multiline, toast}) {
    const t = useT();
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            toast.current?.show({severity: "success", summary: t("forms.toast.copied"), life: 1500, className: "rtl text-xs"});
        } catch {
            toast.current?.show({severity: "warn", summary: t("forms.toast.copy-failed"), className: "rtl text-xs"});
        }
    };
    return (
        <div className="flex gap-2 align-items-start">
            {multiline
                ? <InputTextarea dir="ltr" className="flex-1 text-xs border-round-lg" value={value} readOnly rows={3}/>
                : <InputText dir="ltr" className="flex-1 text-xs border-round-lg h-3rem" value={value} readOnly onFocus={(event) => event.target.select()}/>}
            <Button type="button" outlined className="text-xs gap-2 border-round-lg h-3rem flex-none" icon={<Icon icon="mi mi:content_copy" className="text-base"/>} label={t("forms.copy")} onClick={copy}/>
        </div>
    );
}

export default function FormBuilder() {
    const t = useT();
    const format = useFormat();
    const {id} = useParams();
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const access = useFormsAccess(currentWorkspace());
    const toast = useRef(null);
    const [form, setForm] = useState(null);
    const [draft, setDraft] = useState(null);
    const [options, setOptions] = useState(null);
    const [error, setError] = useState("");
    const [selected, setSelected] = useState(null);
    const [save, setSave] = useState({state: "saved", at: null});
    const [publishing, setPublishing] = useState(null);
    const [preview, setPreview] = useState(null);
    const [confirmRotate, setConfirmRotate] = useState(false);
    const revision = useRef(0);
    const pending = useRef(null);
    const timer = useRef(null);
    const step = STEPS.some(([key]) => key === params.get("step")) ? params.get("step") : "design";
    const readOnly = !form?.canManage;

    const load = useCallback(() => formsApi.get(id).then((next) => {
        setForm(next);
        setDraft(next.draft);
        revision.current = next.revision;
        return next;
    }), [id]);

    useEffect(() => {
        if (!access?.allowed) return;
        load().catch((loadError) => setError(formsErrorKey(loadError)));
        formsApi.options().then(setOptions).catch(() => setOptions({members: [], projects: [], crmAvailable: false}));
    }, [access, load]);

    const flush = useCallback(async () => {
        clearTimeout(timer.current);
        const next = pending.current;
        if (!next) return true;
        pending.current = null;
        setSave({state: "saving"});
        try {
            const result = await formsApi.saveDraft(id, next, revision.current);
            revision.current = result.revision;
            setSave({state: "saved", at: result.savedAt});
            return true;
        } catch (saveError) {
            pending.current = pending.current || next;
            setSave({state: "error", key: formsErrorKey(saveError, "forms.save.failed")});
            return false;
        }
    }, [id]);

    useEffect(() => {
        const beforeUnload = (event) => {
            if (pending.current) { event.preventDefault(); event.returnValue = ""; }
        };
        window.addEventListener("beforeunload", beforeUnload);
        return () => { window.removeEventListener("beforeunload", beforeUnload); flush(); };
    }, [flush]);

    const title = draft?.meta?.title;
    useEffect(() => {
        if (title) document.title = `${title} - ${t("forms.title")}`;
    }, [title, t]);

    const rulesByField = useMemo(() => {
        const out = {};
        (draft?.rules || []).forEach((rule) => (rule.then || []).forEach((action) => { if (action.field) out[action.field] = true; }));
        return out;
    }, [draft]);

    if (!access?.allowed) return <PlanGate access={access}/>;
    if (error) {
        return (
            <div className="h-full w-full flex align-items-center justify-content-center surface-ground p-3">
                <div className="surface-card border-1 border-200 border-round-2xl flex flex-column align-items-center gap-3 p-5">
                    <Message severity="error" text={t(error)}/>
                    <Button type="button" outlined className="text-xs gap-2" icon={<Icon icon="mi mi:arrow_forward" className="text-base"/>} label={t("forms.back")} onClick={() => navigate("/forms")}/>
                </div>
            </div>
        );
    }
    if (!draft) return <div className="h-full flex align-items-center justify-content-center"><ProgressSpinner strokeWidth="4" className="w-3rem h-3rem"/></div>;

    const change = (next) => {
        if (readOnly) return;
        setDraft(next);
        pending.current = next;
        setSave({state: "dirty"});
        clearTimeout(timer.current);
        timer.current = setTimeout(flush, SAVE_DELAY);
    };
    const field = (draft.fields || []).find((item) => item.key === selected);
    const addField = (type) => {
        const item = newField(type, draft.fields || [], t);
        const index = field ? draft.fields.indexOf(field) + 1 : draft.fields.length;
        const fields = [...draft.fields];
        fields.splice(index, 0, item);
        change({...draft, fields});
        setSelected(item.key);
    };
    const goStep = async (key) => { await flush(); setParams({step: key}); };

    const openPublish = async () => {
        await flush();
        setPublishing({loading: true});
        try {
            setPublishing({report: await formsApi.validate(id), note: ""});
        } catch (validateError) {
            setPublishing(null);
            toast.current?.show({severity: "error", summary: t(formsErrorKey(validateError)), className: "rtl text-xs"});
        }
    };
    const publish = async () => {
        setPublishing((current) => ({...current, busy: true, error: null}));
        try {
            const next = await formsApi.publish(id, publishing.note);
            setForm(next);
            setPublishing({done: true, report: next.report});
        } catch (publishError) {
            const details = formsErrorDetails(publishError);
            setPublishing((current) => ({...current, busy: false, report: details.report || current.report, error: formsErrorKey(publishError)}));
        }
    };
    const lifecycle = async (work, summaryKey) => {
        try {
            setForm(await work());
            toast.current?.show({severity: "success", summary: t(summaryKey), life: 2500, className: "rtl text-xs"});
        } catch (lifecycleError) {
            toast.current?.show({severity: "error", summary: t(formsErrorKey(lifecycleError)), className: "rtl text-xs"});
        }
    };
    const testSubmit = async (answers) => {
        await flush();
        setPreview((current) => ({...current, busy: true, serverErrors: {}, error: null}));
        try {
            const result = await formsApi.test(id, answers);
            setPreview((current) => ({...current, busy: false, ending: {...(result.ending || {}), tracking: result.tracking}, scores: result.scores}));
        } catch (testError) {
            setPreview((current) => ({...current, busy: false, serverErrors: formsErrorDetails(testError).fieldErrors || {}, error: formsErrorKey(testError)}));
        }
    };

    const saveText = save.state === "error" ? t(save.key) : save.state === "dirty" ? t("forms.save.dirty") : save.state === "saving" ? t("forms.save.saving") : save.at ? t("forms.save.saved") : "";
    const previewScores = Object.entries(preview?.scores || {}).filter(([, score]) => score && score.counted);
    return (
        <div className="h-full w-full flex flex-column overflow-hidden surface-ground">
            <Toast ref={toast} position="bottom-left"/>
            <header className="surface-card border-bottom-1 border-200 px-3 py-2 flex flex-wrap align-items-center gap-2">
                <Button type="button" text rounded className="p-2" icon={<Icon icon="mi mi:arrow_forward" className="text-lg"/>} aria-label={t("forms.back")} onClick={async () => { await flush(); navigate("/forms"); }}/>
                <div className="flex flex-column gap-1 min-w-0">
                    <strong className="text-sm text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis">{draft.meta?.title || t("forms.untitled")}</strong>
                    <span className="flex align-items-center gap-2 text-xs text-500">
                        <StatusTag status={form.status}/>
                        {form.version && <span>{t("forms.published-version", {n: format.number(form.version)})}</span>}
                        <span className={save.state === "error" ? "text-red-500" : ""}>{saveText}</span>
                    </span>
                </div>
                <nav className="flex gap-1 mx-auto surface-50 border-round-3xl p-1">
                    {STEPS.map(([key, icon]) => (
                        <Button key={key} type="button" text={step !== key} className="text-xs gap-2 border-round-3xl py-2" icon={<Icon icon={icon} className="text-base"/>}
                                label={t(`forms.step.${key}`)} onClick={() => goStep(key)}/>
                    ))}
                </nav>
                <Button type="button" outlined className="text-xs gap-2" icon={<Icon icon="mi mi:visibility" className="text-base"/>} label={t("forms.preview.button")} onClick={() => setPreview({round: Date.now()})}/>
                {!readOnly && <Button type="button" className="text-xs gap-2" icon={<Icon icon="mi mi:send" className="text-base"/>} label={t(form.version ? "forms.publish.new-version" : "forms.publish.button")} onClick={openPublish}/>}
            </header>
            {readOnly && <Message severity="info" className="m-2 justify-content-start text-xs" text={t("forms.builder.read-only")}/>}
            {step === "design" && (
                <div className="flex flex-1 min-h-0">
                    {!readOnly && <Palette onAdd={addField}/>}
                    <Canvas draft={draft} selected={selected} onSelect={setSelected} onChange={change} readOnly={readOnly} rulesByField={rulesByField}/>
                    <aside className="w-20rem flex-none surface-card border-right-1 border-200 overflow-auto p-3 flex flex-column gap-3">
                        <strong className="text-sm text-800">{t(field ? "forms.builder.question-settings" : "forms.builder.form-settings")}</strong>
                        {field
                            ? <FieldInspector key={field.key} field={field} draft={draft} readOnly={readOnly} onChange={(next) => change({...draft, fields: draft.fields.map((item) => (item.key === field.key ? next : item))})}/>
                            : <FormSettings draft={draft} onChange={change} readOnly={readOnly}/>}
                    </aside>
                </div>
            )}
            {step === "logic" && (
                <div className="flex-1 min-h-0 overflow-auto p-3">
                    <div className="w-full xl:w-9 mx-auto flex flex-column gap-3">
                        <DestinationEditor draft={draft} onChange={change} options={options} readOnly={readOnly}/>
                        <FillRulesEditor draft={draft} onChange={change} readOnly={readOnly}/>
                        <ScoresEditor draft={draft} onChange={change} readOnly={readOnly}/>
                        <PostSubmitEditor draft={draft} onChange={change} options={options} readOnly={readOnly}/>
                    </div>
                </div>
            )}
            {step === "responses" && (
                <div className="flex-1 min-h-0 overflow-auto p-3 flex flex-column gap-3">
                    {form.link && (
                        <Section title={t(form.link.active ? "forms.link.title" : "forms.link.title-off")} end={!readOnly && (
                            <div className="flex gap-2">
                                {form.status === "published"
                                    ? <Button type="button" text className="text-xs gap-2" icon={<Icon icon="mi mi:pause" className="text-base"/>} label={t("forms.action.disable")} onClick={() => lifecycle(() => formsApi.status(id, "disable"), "forms.toast.disabled")}/>
                                    : form.status === "disabled" && <Button type="button" text className="text-xs gap-2" icon={<Icon icon="mi mi:play_arrow" className="text-base"/>} label={t("forms.action.enable")} onClick={() => lifecycle(() => formsApi.status(id, "enable"), "forms.toast.enabled")}/>}
                                <Button type="button" text severity="danger" className="text-xs gap-2" icon={<Icon icon="mi mi:autorenew" className="text-base"/>} label={t("forms.link.rotate")} onClick={() => setConfirmRotate(true)}/>
                            </div>
                        )}>
                            <CopyField value={form.link.url} toast={toast}/>
                        </Section>
                    )}
                    <Submissions formKey={id} forms={[{key: id, title: draft.meta?.title || form.title}]} canManage={form.canManage} initialSubmission={params.get("submission")}/>
                </div>
            )}

            <Dialog {...DIALOG} visible={confirmRotate} onHide={() => setConfirmRotate(false)} header={<span className="text-sm">{t("forms.link.rotate")}</span>} className={`${DIALOG.className} w-11 md:w-5 lg:w-3`}
                    footer={(
                        <div className="flex gap-2 justify-content-end pt-3">
                            <Button type="button" text className="text-xs" label={t("forms.cancel")} onClick={() => setConfirmRotate(false)}/>
                            <Button type="button" severity="danger" className="text-xs gap-2" icon={<Icon icon="mi mi:autorenew" className="text-base"/>} label={t("forms.link.rotate-confirm")}
                                    onClick={() => { setConfirmRotate(false); lifecycle(() => formsApi.rotateLink(id), "forms.toast.link-rotated"); }}/>
                        </div>
                    )}>
                <span className="text-xs text-600 line-height-3">{t("forms.link.rotate-warning")}</span>
            </Dialog>

            <Dialog {...DIALOG} visible={Boolean(preview)} onHide={() => setPreview(null)} maximizable header={<span className="text-sm">{t("forms.preview.title")}</span>} className={`${DIALOG.className} w-11 md:w-8 lg:w-6`}>
                {preview && (
                    <div className="flex flex-column gap-3">
                        <Message severity="info" className="w-full justify-content-start text-xs" text={t("forms.preview.note")}/>
                        {preview.ending ? (
                            <>
                                <EndingView ending={preview.ending} onRestart={() => setPreview({round: Date.now()})}/>
                                {previewScores.length > 0 && (
                                    <Section title={t("forms.detail.scores")}>
                                        {previewScores.map(([key, score]) => (
                                            <div key={key} className="flex justify-content-between text-xs">
                                                <span className="text-600">{score.label || key}</span>
                                                <span className="text-800">{format.number(score.normalized ?? score.raw)}{score.bandLabel ? ` · ${score.bandLabel}` : ""}</span>
                                            </div>
                                        ))}
                                    </Section>
                                )}
                            </>
                        ) : (
                            <div className="surface-card border-1 border-200 border-round-2xl border-top-3 border-primary p-4">
                                <FormRenderer key={preview.round} definition={publicDefinition(draft)} onSubmit={readOnly ? undefined : testSubmit} submitting={preview.busy}
                                              serverErrors={preview.serverErrors} disabled={readOnly} submitLabel={t("forms.preview.submit")}/>
                                {preview.error && <Message severity="error" className="w-full justify-content-start text-xs mt-3" text={t(preview.error)}/>}
                            </div>
                        )}
                    </div>
                )}
            </Dialog>

            <Dialog {...DIALOG} visible={Boolean(publishing)} onHide={() => setPublishing(null)} className={`${DIALOG.className} w-11 md:w-7 lg:w-5`}
                    header={<span className="text-sm">{t(publishing?.done ? "forms.publish.done-title" : "forms.publish.review-title")}</span>}
                    footer={publishing && !publishing.done && !publishing.loading ? (
                        <div className="flex gap-2 justify-content-end pt-3">
                            <Button type="button" text className="text-xs" label={t("forms.publish.back")} onClick={() => setPublishing(null)}/>
                            <Button type="button" className="text-xs gap-2" icon={<Icon icon="mi mi:send" className="text-base"/>} label={t("forms.publish.button")} loading={publishing.busy} disabled={!publishing.report?.ready} onClick={publish}/>
                        </div>
                    ) : null}>
                {publishing?.loading && <div className="flex justify-content-center p-4"><ProgressSpinner strokeWidth="4" className="w-3rem h-3rem"/></div>}
                {publishing && !publishing.loading && !publishing.done && (
                    <div className="flex flex-column gap-3">
                        <ReportLines report={publishing.report}/>
                        {publishing.error && <Message severity="error" className="w-full justify-content-start text-xs" text={t(publishing.error)}/>}
                        <Field label={t("forms.publish.note")}>
                            <InputText className="w-full text-xs border-round-lg" value={publishing.note || ""} placeholder={t("forms.publish.note-placeholder")} onChange={(event) => setPublishing((current) => ({...current, note: event.target.value}))}/>
                        </Field>
                        {form.version && <span className="text-xs text-400 line-height-3">{t("forms.publish.versions-note")}</span>}
                    </div>
                )}
                {publishing?.done && form.link && (
                    <div className="flex flex-column gap-3">
                        <span className="text-xs text-700">{t("forms.publish.done", {n: format.number(form.version)})}</span>
                        <CopyField value={form.link.url} toast={toast}/>
                        <Field label={t("forms.publish.embed")}><CopyField value={form.link.embed} multiline toast={toast}/></Field>
                        {(publishing.report?.warnings || []).length > 0 && <ReportLines report={{errors: [], warnings: publishing.report.warnings, notes: []}}/>}
                        <div className="flex gap-2">
                            <Button type="button" outlined className="text-xs gap-2" icon={<Icon icon="mi mi:open_in_new" className="text-base"/>} label={t("forms.publish.open")} onClick={() => window.open(form.link.url, "_blank", "noopener")}/>
                            <Button type="button" text className="text-xs gap-2" icon={<Icon icon="mi mi:inbox" className="text-base"/>} label={t("forms.publish.to-responses")} onClick={() => { setPublishing(null); goStep("responses"); }}/>
                        </div>
                    </div>
                )}
            </Dialog>
        </div>
    );
}
